import type { z } from "zod/v4";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import type {
  BuildVizElementArgs,
  BuiltVizElement,
} from "@/lib/ai/visualization/views/shared/element-builder";
import { buildSeriesLabelMap } from "@/lib/ai/visualization/views/shared/label-map";
import { resolveRankOnlyAxisDirections } from "@/lib/ai/visualization/views/shared/presentation-defaults";
import { buildSeriesEntries } from "@/lib/ai/visualization/views/shared/series-entries";
import { validateTimeSeriesLikeSpec } from "@/lib/ai/visualization/views/shared/spec-validation";
import type { VizAuthoringCardDraft } from "@/lib/ai/visualization/views/shared/view-definition";
import type { timeSeriesAreaAuthoringSchema } from "@/lib/ai/visualization/views/time-series-area/schema";

type TimeSeriesAreaSpec = Extract<VizSpec, { type: "time-series-area" }>;
type TimeSeriesAreaAuthoring = z.infer<typeof timeSeriesAreaAuthoringSchema>;

/**
 * area chart は shared rule で validation できるため、その dispatch を local contract として公開する。
 */
export const validateTimeSeriesAreaSpec = validateTimeSeriesLikeSpec;

/**
 * structured authoring payload を `time-series-area` 用の renderer-ready spec へ変換する。
 */
export function compileTimeSeriesAreaSpec(args: {
  datasetId: string;
  dataset: BuildVizElementArgs<TimeSeriesAreaSpec>["dataset"];
  card: VizAuthoringCardDraft | null;
  view: TimeSeriesAreaAuthoring;
}): TimeSeriesAreaSpec {
  const axisDirections = resolveRankOnlyAxisDirections(
    args.dataset,
    args.view.seriesKeys,
  );

  return {
    type: "time-series-area",
    datasetId: args.datasetId,
    cardTitle: args.card?.title ?? null,
    xKey: args.view.xKey,
    seriesKeys: args.view.seriesKeys,
    componentProps: {
      xLabel: args.view.xLabel,
      ...(axisDirections ? { axisDirections } : null),
      seriesLabels: buildSeriesLabelMap({
        seriesKeys: args.view.seriesKeys,
        entries: args.view.seriesLabelEntries,
      }),
    },
  };
}

/**
 * time-series-area 用の UI element を構築する。
 */
export function buildTimeSeriesAreaElement(
  args: BuildVizElementArgs<TimeSeriesAreaSpec>,
): BuiltVizElement {
  const seriesLabels = args.spec.componentProps?.seriesLabels ?? {};

  return {
    elementKey: args.chartKey,
    element: {
      type: "TimeSeriesArea",
      props: {
        dataPath: args.dataPath,
        xKey: args.spec.xKey,
        series: buildSeriesEntries(
          args.dataset,
          args.spec.seriesKeys,
          seriesLabels,
        ),
        ...(args.spec.componentProps ?? {}),
      },
      children: [],
    },
  };
}
